/**
 * MEDIA-HEIC-SERVER-PREVIEW-PROFILE-A1 — authenticated server HEIC preview request.
 * Uses the shared axios API client (existing refresh/session behavior).
 */

import api from "../../api/axios";
import {
  SERVER_PREVIEW_ENDPOINT,
  SERVER_PREVIEW_TIMEOUT_MS,
} from "./constants";

export class ServerPreviewError extends Error {
  constructor(code, detail, status) {
    super(detail || code || "processing_failed");
    this.name = "ServerPreviewError";
    this.code = code || "processing_failed";
    this.detail = detail || this.message;
    this.status = status;
  }
}

/**
 * POST original HEIC/HEIF to /api/media/image-preview/.
 * Returns an ephemeral JPEG File. Does not persist.
 *
 * @param {File} file
 * @param {string} surface
 * @param {{ signal?: AbortSignal, timeoutMs?: number, apiClient?: typeof api }} [options]
 */
export async function requestServerImagePreview(file, surface, options = {}) {
  if (!file) {
    throw new ServerPreviewError("missing_file", "Потрібен файл зображення.");
  }
  if (!surface) {
    throw new ServerPreviewError(
      "invalid_surface",
      "Невідома поверхня попереднього перегляду.",
    );
  }

  const client = options.apiClient || api;
  const timeoutMs =
    options.timeoutMs != null ? options.timeoutMs : SERVER_PREVIEW_TIMEOUT_MS;
  const outerSignal = options.signal;

  const timeoutController = new AbortController();
  let timeoutId = null;
  const onOuterAbort = () => {
    timeoutController.abort();
  };

  if (outerSignal) {
    if (outerSignal.aborted) {
      timeoutController.abort();
    } else {
      outerSignal.addEventListener("abort", onOuterAbort, { once: true });
    }
  }

  if (timeoutMs > 0) {
    timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
  }

  const form = new FormData();
  form.append("image", file);
  form.append("surface", surface);

  try {
    const response = await client.post(SERVER_PREVIEW_ENDPOINT, form, {
      signal: timeoutController.signal,
      responseType: "blob",
      // Let the browser set multipart boundary; do not force Content-Type.
      headers: { "Content-Type": undefined },
      timeout: timeoutMs,
    });

    const blob = response.data;
    if (!blob || !(blob instanceof Blob) || blob.size <= 0) {
      throw new ServerPreviewError(
        "processing_failed",
        "Тимчасова помилка обробки. Спробуйте пізніше.",
        response.status,
      );
    }

    const type = (blob.type || response.headers?.["content-type"] || "").toLowerCase();
    if (type && !type.includes("jpeg") && !type.includes("jpg")) {
      // Error payloads may arrive as JSON Blob when responseType=blob.
      if (type.includes("json") || type.includes("text")) {
        const text = await blob.text();
        let parsed = null;
        try {
          parsed = JSON.parse(text);
        } catch (_err) {
          parsed = null;
        }
        throw new ServerPreviewError(
          parsed?.code || "processing_failed",
          parsed?.detail || "Тимчасова помилка обробки. Спробуйте пізніше.",
          response.status,
        );
      }
    }

    const FileCtor = options.File || (typeof File !== "undefined" ? File : null);
    if (!FileCtor) {
      throw new ServerPreviewError(
        "processing_failed",
        "Тимчасова помилка обробки. Спробуйте пізніше.",
      );
    }

    return new FileCtor([blob], "preview.jpg", {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    if (err instanceof ServerPreviewError) throw err;
    if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || timeoutController.signal.aborted) {
      const cancelErr = new ServerPreviewError(
        "cancelled_ui",
        "Запит скасовано.",
      );
      cancelErr.aborted = true;
      throw cancelErr;
    }

    const status = err?.response?.status;
    let code = "processing_failed";
    let detail = "Тимчасова помилка обробки. Спробуйте пізніше.";
    const data = err?.response?.data;

    const parseJsonBlob = async (value) => {
      if (!value) return null;
      if (typeof value === "object" && !(value instanceof Blob) && value.code) {
        return value;
      }
      // Duck-typed blob (Jest / axios responseType=blob error bodies).
      if (value && typeof value.text === "function") {
        try {
          return JSON.parse(await value.text());
        } catch (_e) {
          return null;
        }
      }
      if (typeof Blob !== "undefined" && value instanceof Blob) {
        try {
          return JSON.parse(await value.text());
        } catch (_e) {
          return null;
        }
      }
      if (typeof value === "string") {
        try {
          return JSON.parse(value);
        } catch (_e) {
          return null;
        }
      }
      return null;
    };

    const parsed = await parseJsonBlob(data);
    if (parsed?.code) {
      code = parsed.code;
      detail = parsed.detail || detail;
    } else if (status === 401) {
      code = "not_authenticated";
      detail = "Необхідна автентифікація.";
    } else if (status === 429) {
      code = "throttled";
      detail = "Забагато запитів. Спробуйте пізніше.";
    } else if (status === 503) {
      code = "processing_failed";
    }

    throw new ServerPreviewError(code, detail, status);
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
    if (outerSignal) {
      outerSignal.removeEventListener("abort", onOuterAbort);
    }
  }
}
